import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { HiArrowRight, HiArrowLeft } from "react-icons/hi";
import { MdCalendarToday } from "react-icons/md";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";

import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslateItems } from "../../hooks/useTranslate";

// ─── Teks UI statis ────────────────────────────────────────────────────────────
const UI_TEXT = {
  EN: {
    // Hero
    pubLabel:    "Publication",
    heroTitle:   "Latest News",
    heroHighlight: "& Stories",
    heroSub:     "Discover case studies, in-depth insights, and the latest news from Rumoh Pangan Aceh's food security programs.",

    // Filter bar
    categories:  ["View All", "Case Study", "News", "Article", "Post"],
    catLabels: {
      "View All":   "View All",
      "Case Study": "Case Study",
      "News":       "News",
      "Article":    "Article",
      "Post":       "Post",
    },
    postCount:   "posts",
    articleCount:"articles",
    noArticle:   "No articles in this category yet.",

    // INSight
    insightSub:  "Insights directly from our Instagram",
    followBtn:   "Follow us",

    // Post card
    readMore:    "Continue Reading",

    // Pagination
    resultCount: (n) => `${n} articles`,
  },

  ID: {
    pubLabel:    "Publikasi",
    heroTitle:   "Berita & Cerita",
    heroHighlight: "Terbaru",
    heroSub:     "Temukan studi kasus, wawasan mendalam, dan berita terkini dari program ketahanan pangan Rumoh Pangan Aceh.",

    categories:  ["View All", "Case Study", "News", "Article", "Post"],
    catLabels: {
      "View All":   "View All",
      "Case Study": "Case Study",
      "News":       "News",
      "Article":    "Article",
      "Post":       "Post",
    },
    postCount:   "postingan",
    articleCount:"artikel",
    noArticle:   "Belum ada artikel dalam kategori ini.",

    insightSub:  "Wawasan langsung dari Instagram kami",
    followBtn:   "Ikuti kami",

    readMore:    "Selengkapnya",

    resultCount: (n) => `${n} artikel`,
  },
};

const PER_PAGE = 6;

// ─── Helpers ───────────────────────────────────────────────────────────────────
const useInView = (ref, threshold = 0.08) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

const formatDate = (iso, language) =>
  new Date(iso).toLocaleDateString(language === "EN" ? "en-US" : "id-ID", {
    day: "numeric", month: "long", year: "numeric",
  });

const stripHtml = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toSlug = (text) =>
  text
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // hilangkan diakritik
    .replace(/[^a-z0-9\s-]/g, "")  // hapus karakter selain huruf/angka/spasi/dash
    .trim()
    .replace(/\s+/g, "-");

// ─── Post Card ─────────────────────────────────────────────────────────────────
const PostCard = ({ post, index, inView, tx, toSlug }) => {
  const navigate = useNavigate();
  const categoryColors = {
    "Case Study": "bg-[#1a4a35] text-white",
    "News":       "bg-[#3a3a34] text-white",
    "Article":    "bg-[#7c3aed] text-white",
  };
  const { language } = useLanguage();

  return (
    <div
      className="bg-white border border-[#e8e0d0] flex flex-col group hover:border-[#c9862a]/40 hover:shadow-xl transition-all duration-400 overflow-hidden"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.6s ease ${index * 0.08}s, transform 0.6s ease ${index * 0.08}s, border-color 0.3s, box-shadow 0.3s`,
      }}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-52">
        {post.url ? (
        <img
          src={post.url}
          alt={post.judul}
          className="w-full h-full object-cover brightness-90 group-hover:brightness-100 group-hover:scale-105 transition-all duration-700"
        />
        ) : (
          <div className="w-full h-full bg-[#f7f3ec] flex items-center justify-center text-[#a09880]">
            No Image
          </div>
        )}
        <div className="absolute top-4 left-4">
          <span className={`text-[10px] font-bold tracking-[2px] uppercase px-3 py-1.5 ${categoryColors[post.kategori] ?? "bg-[#c9862a] text-white"}`}>
            {post.kategori}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-[#c9862a] group-hover:w-full transition-all duration-500" />
      </div>

      {/* Body */}
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-[#a09880] text-xs mb-4">
          <MdCalendarToday className="text-[#c9862a]" size={12} />
          <span>{formatDate(post.createdAt, language)}</span>
        </div>

        {/* judul → sudah diterjemahkan */}
        <h3 className="font-serif text-lg font-bold text-[#1a1a18] leading-snug mb-3 group-hover:text-[#1a4a35] transition-colors duration-300">
          {post.judul}
        </h3>

        <div className="w-6 h-[2px] bg-[#c9862a]/40 mb-4 group-hover:w-12 transition-all duration-300" />

        {/* deskripsi → sudah plain text (di-strip di useTranslate) */}
        <p className="text-[#6b6456] text-sm leading-relaxed flex-1 line-clamp-3">
          {stripHtml(post.deskripsi)}
        </p>

        <div className="mt-6 pt-5 border-t border-[#e8e0d0]">
          <button
            onClick={() => navigate(`/view-stories/${toSlug(post.judul)}`)}
            className="flex items-center gap-2 border border-[#c9862a] text-[#1a1a18] text-xs font-bold tracking-widest uppercase px-5 py-2.5 hover:bg-[#c9862a] hover:text-white transition-all duration-200 group/btn"
          >
            {tx.readMore}
            <HiArrowRight size={12} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Instagram Embed (INSight) ─────────────────────────────────────────────────
const InsightEmbed = ({ inView, posts, tx }) => {
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (!inView || scriptLoaded.current) return;
    if (window.instgrm) { window.instgrm.Embeds.process(); return; }
    const script = document.createElement("script");
    script.src = "https://www.instagram.com/embed.js";
    script.async = true;
    script.defer = true;
    script.onload = () => { if (window.instgrm) window.instgrm.Embeds.process(); };
    document.body.appendChild(script);
    scriptLoaded.current = true;
  }, [inView]);

  return (
    <div>
      {/* Header */}
      <div
        className="flex flex-col sm:flex-row sm:items-center gap-4 mb-10"
        style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(20px)", transition: "opacity 0.6s ease 0s, transform 0.6s ease 0s" }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #f58529 0%, #dd2a7b 50%, #515bd4 100%)" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
            </svg>
          </div>
          <div>
            <p className="text-[#1a1a18] font-bold text-sm tracking-wide">INSight</p>
            {/* Subtitle ikut bahasa */}
            <p className="text-[#a09880] text-xs">{tx.insightSub}</p>
          </div>
        </div>

        <div className="hidden sm:block flex-1 h-px bg-[#e8e0d0]" />

        <a
          href="https://www.instagram.com/rumohpanganaceh/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-5 py-2.5 transition-all duration-200 flex-shrink-0"
          style={{ border: "1px solid", borderImageSlice: 1, borderImageSource: "linear-gradient(135deg, #f58529, #dd2a7b, #515bd4)", color: "#dd2a7b" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "linear-gradient(135deg, #f58529, #dd2a7b, #515bd4)"; e.currentTarget.style.color = "white"; e.currentTarget.style.borderImageSource = "none"; e.currentTarget.style.border = "1px solid transparent"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#dd2a7b"; e.currentTarget.style.border = "1px solid"; e.currentTarget.style.borderImageSlice = "1"; e.currentTarget.style.borderImageSource = "linear-gradient(135deg, #f58529, #dd2a7b, #515bd4)"; }}
        >
          {tx.followBtn}
          <HiArrowRight size={12} />
        </a>
      </div>

      {/* Instagram Embeds */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 justify-items-center">
        {posts.map((post, index) => (
          <div
            key={post.id}
            className="w-full flex justify-center"
            style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : "translateY(32px)", transition: `opacity 0.6s ease ${index * 0.12 + 0.2}s, transform 0.6s ease ${index * 0.12 + 0.2}s` }}
          >
            <blockquote
              className="instagram-media"
              data-instgrm-permalink={post.url}
              data-instgrm-version="14"
              data-instgrm-captioned
              style={{ background: "#FFF", border: "0", borderRadius: "3px", boxShadow: "0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)", margin: "1px", maxWidth: "540px", minWidth: "326px", padding: "0", width: "calc(100% - 2px)" }}
            >
              <div style={{ padding: "16px" }}>
                <a href={post.urlInstagram} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: "12px", textDecoration: "none", color: "#1a1a18" }}>
                  <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "linear-gradient(135deg, #f58529, #dd2a7b, #515bd4)" }} />
                  <div>
                    <p style={{ margin: 0, fontWeight: "bold", fontSize: "14px" }}>rumohpangan</p>
                    <p style={{ margin: 0, color: "#a09880", fontSize: "12px" }}>Lihat postingan ini di Instagram</p>
                  </div>
                </a>
              </div>
            </blockquote>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Pagination ────────────────────────────────────────────────────────────────
const Pagination = ({ current, total, onChange }) => {
  const pages = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else if (current <= 4) {
    pages.push(1, 2, 3, 4, 5, "...", total);
  } else if (current >= total - 3) {
    pages.push(1, "...", total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, "...", current - 1, current, current + 1, "...", total);
  }

  const btnBase = "w-9 h-9 flex items-center justify-center text-sm font-bold transition-all duration-200";

  return (
    <div className="flex items-center justify-center gap-1 pt-12 pb-4">
      <button onClick={() => onChange(1)} disabled={current === 1} className={`${btnBase} border border-[#e8e0d0] text-[#6b6456] hover:border-[#c9862a] hover:text-[#c9862a] disabled:opacity-30 disabled:cursor-not-allowed text-xs`}>«</button>
      <button onClick={() => onChange(current - 1)} disabled={current === 1} className={`${btnBase} border border-[#e8e0d0] text-[#6b6456] hover:border-[#c9862a] hover:text-[#c9862a] disabled:opacity-30 disabled:cursor-not-allowed`}><HiArrowLeft size={13} /></button>
      <div className="flex items-center gap-1 mx-2">
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="w-9 h-9 flex items-center justify-center text-[#a09880] text-sm select-none">···</span>
          ) : (
            <button key={p} onClick={() => onChange(p)} className={`${btnBase} ${p === current ? "bg-[#1a4a35] text-white border border-[#1a4a35]" : "border border-[#e8e0d0] text-[#6b6456] hover:border-[#c9862a] hover:text-[#c9862a]"}`}>{p}</button>
          )
        )}
      </div>
      <button onClick={() => onChange(current + 1)} disabled={current === total} className={`${btnBase} border border-[#e8e0d0] text-[#6b6456] hover:border-[#c9862a] hover:text-[#c9862a] disabled:opacity-30 disabled:cursor-not-allowed`}><HiArrowRight size={13} /></button>
      <button onClick={() => onChange(total)} disabled={current === total} className={`${btnBase} border border-[#e8e0d0] text-[#6b6456] hover:border-[#c9862a] hover:text-[#c9862a] disabled:opacity-30 disabled:cursor-not-allowed text-xs`}>»</button>
    </div>
  );
};

// ─── Stories Section ───────────────────────────────────────────────────────────
const Stories = () => {
  const { language } = useLanguage();
  const tx = UI_TEXT[language];

  const ref = useRef(null);
  const inView = useInView(ref);
  const [activeCategory, setActiveCategory] = useState("View All");
  const [currentPage, setCurrentPage] = useState(1);
  const [loaded, setLoaded] = useState(false);

  const [rawAllPosts, setRawAllPosts]         = useState([]);
  const [insightPosts, setInsightPosts]       = useState([]);
  const [fetchLoading, setFetchLoading]       = useState(true);

  useEffect(() => { const t = setTimeout(() => setLoaded(true), 80); return () => clearTimeout(t); }, []);

  // 1. Fetch data mentah dari API (hanya sekali)
useEffect(() => {
  const fetchStories = async () => {
    try {
      const apiUrl = process.env.REACT_APP_URL_API;
      const res = await axios.get(`${apiUrl}/all-stories`);
      const data = Array.isArray(res.data) ? res.data : [];

      // 1. Urutkan seluruh data berdasarkan createdAt dari yang TERBARU ke TERLAMA
      const sortedData = [...data].sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      // 2. Filter data dari array yang SUDAH terurut
      setRawAllPosts(sortedData.filter((s) => s.kategori !== "Post"));
      setInsightPosts(sortedData.filter((s) => s.kategori === "Post" && s.urlInstagram));
    } catch (err) {
      console.error("Error fetching stories:", err);
    } finally {
      setFetchLoading(false);
    }
  };

  fetchStories();
}, []);

  // 2. Terjemahkan "judul" dan "deskripsi" via MyMemory
  //    deskripsi mengandung HTML Quill → useTranslateItems sudah strip otomatis
  const { translated: allPosts, loading: translating } = useTranslateItems(
    rawAllPosts,
    language,
    ["deskripsi"]  // deskripsi di-strip HTML di dalam hook
  );

  // Reset halaman saat kategori atau bahasa berubah
  useEffect(() => { setCurrentPage(1); }, [activeCategory, language]);

  const filtered =
    activeCategory === "View All"
      ? allPosts
      : allPosts.filter((s) => s.kategori === activeCategory);

  const totalPages  = Math.ceil(filtered.length / PER_PAGE);
  const paginated   = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const categoryCount = (cat) => {
    if (cat === "Post")     return insightPosts.length;
    if (cat === "View All") return rawAllPosts.length;
    return rawAllPosts.filter((s) => s.kategori === cat).length;
  };

  const handleCategory = (cat) => { setActiveCategory(cat); setCurrentPage(1); };
  const handlePage     = (p)   => { setCurrentPage(p); ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  const isLoading = fetchLoading || translating;

  return (
    <section ref={ref} className="bg-white">

      {/* ── Hero banner ── */}
      <div className="relative bg-[#1a4a35] overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-end pr-8 select-none pointer-events-none overflow-hidden">
          <span className="font-serif text-[180px] font-bold text-white/[0.04] leading-none">STORIES</span>
        </div>
        <div
          className="absolute left-0 top-0 h-full w-1 bg-[#c9862a]"
          style={{ transform: loaded ? "scaleY(1)" : "scaleY(0)", transformOrigin: "top", transition: "transform 1s cubic-bezier(0.16,1,0.3,1) 0.2s" }}
        />
        <div className="max-w-5xl mx-auto px-8 md:px-12 py-16 relative z-10">
          <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s ease 0.3s" }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">{tx.pubLabel}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
              {tx.heroTitle}<br />
              <span className="text-[#c9862a]">{tx.heroHighlight}</span>
            </h1>
            <p className="text-white/60 text-sm mt-4 max-w-md leading-relaxed">{tx.heroSub}</p>
          </div>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="sticky top-0 z-20 bg-white border-b border-[#e8e0d0] shadow-sm">
        <div className="max-w-5xl mx-auto px-8 md:px-12">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none py-1">
            {tx.categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategory(cat)}
                className={`whitespace-nowrap px-5 py-3 text-xs font-bold tracking-[2px] uppercase transition-all duration-200 border-b-2 ${
                  activeCategory === cat
                    ? "border-[#c9862a] text-[#1a1a18]"
                    : "border-transparent text-[#a09880] hover:text-[#3a3a34] hover:border-[#e8e0d0]"
                }`}
              >
                {tx.catLabels[cat] ?? cat}
                {cat !== "View All" && (
                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 font-bold transition-colors duration-200 ${activeCategory === cat ? "bg-[#c9862a] text-white" : "bg-[#f7f3ec] text-[#a09880]"}`}>
                    {categoryCount(cat)}
                  </span>
                )}
              </button>
            ))}

            {/* Result count */}
            <div className="ml-auto pl-4 text-xs text-[#a09880] whitespace-nowrap py-3 border-b-2 border-transparent">
              {activeCategory === "Post"
                ? `${insightPosts.length} ${tx.postCount}`
                : tx.resultCount(filtered.length)}
            </div>
          </div>
        </div>
      </div>

      {/* ── Content Area ── */}
      <div className="max-w-5xl mx-auto px-8 md:px-12 py-12">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-[#f7f3ec] border border-[#e8e0d0] animate-pulse" style={{ height: "360px" }} />
            ))}
          </div>
        ) : activeCategory === "Post" ? (
          <InsightEmbed inView={inView} posts={insightPosts} tx={tx} />
        ) : paginated.length === 0 ? (
          <div className="text-center py-24 text-[#a09880]">
            <div className="font-serif text-5xl text-[#e8e0d0] mb-4">✳</div>
            <p className="text-sm">{tx.noArticle}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((post, i) => (
                <PostCard key={post.id} post={post} index={i} inView={inView} tx={tx} toSlug={toSlug} />
              ))}
            </div>
            {totalPages > 1 && (
              <>
                <div className="flex items-center gap-4 mt-12">
                  <div className="flex-1 h-px bg-[#e8e0d0]" />
                  <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
                  <div className="flex-1 h-px bg-[#e8e0d0]" />
                </div>
                <Pagination current={currentPage} total={totalPages} onChange={handlePage} />
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const StoriesPage = () => (
  <main className="overflow-x-hidden font-homepage bg-white">
    <Navbar />
    <section id="stories"><Stories /></section>
    <section id="footer"><Footer /></section>
  </main>
);

export default StoriesPage;